"use client"

import { useState } from "react"
import { X } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { DatePicker } from "@/components/ui/date-picker"
import { DecimalInput } from "@/components/ui/decimal-input"
import { useLanguage } from "@/contexts/language-context"
import { useFormatters } from "@/hooks/use-formatters"
import { parseDateOnly } from "@/lib/utils"
import type { ScheduledBudgetedHoursEntryFormData as Entry } from "@/lib/schemas"
import {
  cancelEntry,
  currentHoursAppliedDate,
  dayAfter,
  entryProblems,
  isAfterLastDay,
  isPending,
  toDateOnly,
  type EntryBounds,
} from "@/lib/utils/scheduled-budgeted-hours"

export interface ScheduleBudgetedHoursDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The worker's whole list, applied and cancelled entries included. Only pending ones are shown. */
  entries: Entry[]
  currentHours: number | null
  bounds: EntryBounds
  onEntriesChange: (entries: Entry[]) => void
}

/**
 * Lists a worker's pending changes to budgeted hours per day, and adds or cancels them. Works on the worker
 * dialog's own copy of the list, so nothing reaches the engagement until the worker and the engagement are
 * both saved.
 */
export const ScheduleBudgetedHoursDialog = ({
  open,
  onOpenChange,
  entries,
  currentHours,
  bounds,
  onEntriesChange,
}: ScheduleBudgetedHoursDialogProps) => {
  const { dictionary } = useLanguage()
  const { formatDate } = useFormatters()
  const t = dictionary?.engagement?.workers

  const [date, setDate] = useState<Date | null>(null)
  const [hours, setHours] = useState<number | null>(null)
  const [errors, setErrors] = useState<Partial<Record<'effectiveDate' | 'hoursPerDay', string>>>({})

  // Positions are kept so a cancel reaches the right entry in the full list.
  const pending = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => isPending(entry))
    .sort((a, b) => a.entry.effectiveDate.localeCompare(b.entry.effectiveDate))

  const resetAddRow = () => {
    setDate(null)
    setHours(null)
    setErrors({})
  }

  const handleAdd = () => {
    const nextErrors: typeof errors = {}
    if (!date) {
      nextErrors.effectiveDate = "Date is required"
    }
    if (hours == null) {
      nextErrors.hoursPerDay = "Field is required"
    }

    if (date && hours != null) {
      const effectiveDate = toDateOnly(date)
      for (const problem of entryProblems({ effectiveDate, hoursPerDay: hours }, bounds)) {
        nextErrors[problem.field] ??= problem.message
      }
      // Applied changes count too, as in the worker schema and the service: one applied today shares the
      // only date a new change can clash with it on.
      if (entries.some(entry => !entry.cancelledDate && entry.effectiveDate === effectiveDate)) {
        nextErrors.effectiveDate ??= `More than one budgeted hours change takes effect on ${effectiveDate}.`
      }
    }

    if (Object.keys(nextErrors).length > 0 || !date || hours == null) {
      setErrors(nextErrors)
      return
    }

    onEntriesChange([
      ...entries,
      { id: null, effectiveDate: toDateOnly(date), hoursPerDay: hours, appliedDate: null, cancelledDate: null },
    ])
    resetAddRow()
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resetAddRow()
    }
    onOpenChange(next)
  }

  const currentSince = currentHoursAppliedDate(entries, currentHours)

  const earliest = dayAfter(bounds.today)
  const minDate = parseDateOnly(bounds.firstDay && bounds.firstDay > earliest ? bounds.firstDay : earliest) ?? undefined
  const maxDate = parseDateOnly(bounds.lastDay) ?? undefined

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t?.scheduledChangesTitle || "Scheduled Budgeted Hours Changes"}</DialogTitle>
          <DialogDescription>
            {t?.scheduledChangesDescription || "Each change sets the worker's budgeted hours per day from its effective date. Past changes are in the engagement history."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="w-fit rounded-md bg-muted px-3 py-1.5 text-sm">
            <span className="text-muted-foreground">{t?.currentBudgetedHours || "Current"}: </span>
            <span className="font-medium">
              {currentHours == null
                ? "-"
                : currentSince
                  ? (t?.hoursPerDayFrom || "{hours} h/day from {date}")
                      .replace("{hours}", String(currentHours))
                      .replace("{date}", formatDate(parseDateOnly(currentSince)))
                  : (t?.hoursPerDayValue || "{hours} h/day").replace("{hours}", String(currentHours))}
            </span>
          </div>

          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t?.noScheduledChanges || "No scheduled changes."}</p>
          ) : (
            // Capped so the entry row and Done stay in view however many changes are scheduled.
            <ul className="max-h-60 space-y-2 overflow-y-auto pr-1">
              {pending.map(({ entry, index }) => (
                <li key={entry.id ?? `new-${entry.effectiveDate}`} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                  <span>
                    {(t?.hoursPerDayFrom || "{hours} h/day from {date}")
                      .replace('{hours}', String(entry.hoursPerDay))
                      .replace('{date}', formatDate(parseDateOnly(entry.effectiveDate)))}
                    {isAfterLastDay(entry, bounds.lastDay) && (
                      <span className="ml-2 text-xs text-muted-foreground italic">
                        ({t?.afterLastDayOfWork || "after last day of work"})
                      </span>
                    )}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t?.cancelChange || "Cancel change"}
                    onClick={() => onEntriesChange(cancelEntry(entries, index, bounds.today))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2">
            <Label className="text-sm font-medium">{t?.effectiveDate || "Effective Date"}</Label>
            <Label className="text-sm font-medium">{t?.hoursPerDay || "Hours per Day"}</Label>
            <span />
            <DatePicker
              value={date}
              onChange={setDate}
              placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
              minDate={minDate}
              maxDate={maxDate}
              allowTextInput
            />
            <DecimalInput value={hours} onChange={setHours} decimalPlaces={2} minDecimalPlaces={0} placeholder="Enter hours" />
            <Button type="button" onClick={handleAdd}>
              {t?.save || "Save"}
            </Button>
            {(errors.effectiveDate || errors.hoursPerDay) && (
              <>
                <p className="text-sm font-medium text-destructive">{errors.effectiveDate}</p>
                <p className="text-sm font-medium text-destructive">{errors.hoursPerDay}</p>
                <span />
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t?.done || "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
